import { useEffect, useState, useCallback } from "react";
import SidebarAdmin from "./sidebar-admin";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

// ── Types ────────────────────────────────────────────────────────────────
interface Client {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string | number;
  type: "individual" | "company" | string;
  company_name?: string;
  address?: string;
  country?: string;
}

interface CsvColumn {
  index: number;
  name: string;
  sampleData: string;
}

// ── Styles ──────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "13px",
  outline: "none",
  color: "#0f172a",
  backgroundColor: "#f8fafc",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#294b5a",
  letterSpacing: "0.04em",
  marginBottom: "5px",
  display: "block",
};

const primaryBtn: React.CSSProperties = {
  backgroundColor: "#294b5a",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "10px 18px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

const ghostBtn: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#294b5a",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "10px 18px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(15, 23, 42, 0.55)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1050,
  padding: "16px",
};

const TypeBadge = ({ type }: { type: string }) => {
  const isCompany = type === "company";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "11.5px",
        fontWeight: 600,
        backgroundColor: isCompany ? "rgba(211, 190, 118, 0.18)" : "rgba(41, 75, 90, 0.08)",
        color: isCompany ? "#93762f" : "#294b5a",
        textTransform: "capitalize",
      }}
    >
      {type || "individual"}
    </span>
  );
};

const ListClient = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Modales
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Modal Edit Client
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState<Client>({
    id: 0,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    type: "individual",
    company_name: "",
    address: "",
    country: "",
  });

  // Import / Export
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [csvColumns, setCsvColumns] = useState<CsvColumn[]>([]);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [fieldMapping, setFieldMapping] = useState<{ [key: string]: string }>({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    type: "",
    company_name: "",
    address: "",
    country: "",
  });

  const clientFields = [
    { key: "firstname", label: "First name" },
    { key: "lastname", label: "Last name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "type", label: "Type" },
    { key: "company_name", label: "Company name" },
    { key: "address", label: "Address" },
    { key: "country", label: "Country" },
  ];

  // Fetch Clients
  const fetchClients = useCallback(async (page = 1, search = "") => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/clients`, {
        params: { page, search: search || undefined },
      });
      setClients(response.data.data.data || []);
      setCurrentPage(response.data.data.current_page || 1);
      setLastPage(response.data.data.last_page || 1);
      setTotal(response.data.data.total || response.data.data.data.length || 0);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error loading clients list");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients(1, searchTerm);
  }, [fetchClients]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchClients(1, searchTerm);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm, fetchClients]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchClients(page, searchTerm);
    }
  };

  const getPaginationRange = (current: number, totalPages: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;
    try {
      const clientToDelete = clients[selectedRow];
      await axios.delete(`${API_URL}/client/${clientToDelete.id}`);
      setClients((prev) => prev.filter((client) => client.id !== clientToDelete.id));
      toast.success("Client deleted successfully");
      fetchClients(currentPage, searchTerm);
    } catch (error: any) {
      console.error("Error deleting customer:", error);
      toast.error("Error deleting customer");
    } finally {
      setShowDeleteModal(false);
      setSelectedRow(null);
    }
  };

  // ── Open Edit Modal ───────────────────────────────────────────────────
  const handleEditClick = (client: Client) => {
    setEditFormData({
      id: client.id,
      firstname: client.firstname || "",
      lastname: client.lastname || "",
      email: client.email || "",
      phone: client.phone ? String(client.phone) : "",
      type: client.type || "individual",
      company_name: client.company_name || "",
      address: client.address || "",
      country: client.country || "",
    });
    setShowEditModal(true);
  };

  // ── Submit Edit Form (Laravel API) ──────────────────────────────────────
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      const payload: any = {
        firstname: editFormData.firstname,
        lastname: editFormData.lastname,
        email: editFormData.email,
        phone: editFormData.phone,
        type: editFormData.type,
        address: editFormData.address,
        country: editFormData.country,
      };

      if (editFormData.type === "company" && editFormData.company_name) {
        payload.company_name = editFormData.company_name;
      }

      const response = await axios.put(`${API_URL}/client/${editFormData.id}`, payload);

      if (response.data.status) {
        toast.success("Client updated successfully!");
        setShowEditModal(false);
        fetchClients(currentPage, searchTerm);
      } else {
        toast.error(response.data.message || "Failed to update client");
      }
    } catch (error: any) {
      console.error("Error updating client:", error);
      const errMsg = error.response?.data?.message || "Error updating client information";
      toast.error(errMsg);
    } finally {
      setEditLoading(false);
    }
  };

  const handleExportFile = async (format: "excel" | "csv" | "pdf" | "word", extension: string) => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients/export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clients_${new Date().toISOString().split("T")[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} file downloaded successfully`);
      setShowExportModal(false);
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      toast.error(`Error downloading ${format.toUpperCase()} file`);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "24px 32px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* EN-TÊTE PAGE */}
      <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-3">
        <div>
          <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
            Manage Clients
          </h1>
          <p style={{ color: "#64748b", fontSize: "13.5px", margin: "4px 0 0", fontWeight: 400 }}>
            <strong style={{ color: "#294b5a" }}>{total}</strong> client{total !== 1 ? "s" : ""} registered in system.
          </p>
        </div>
      </div>

      {/* BLOC PRINCIPAL */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        {/* RECHERCHE */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7", backgroundColor: "#ffffff" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle, padding: "9px 36px 9px 38px" }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", color: "#94a3b8" }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <div style={{ padding: "60px", textAlign: "center", color: "#64748b", fontSize: "14px" }}>
            <i className="fa-solid fa-circle-notch fa-spin me-2" style={{ color: "#294b5a" }}></i>
            Loading clients list...
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>First Name</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Last Name</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Type</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Company</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Email</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Phone</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.length > 0 ? (
                    clients.map((item, index) => (
                      <tr
                        key={item.id}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                          {item.firstname}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#0f172a" }}>
                          {item.lastname}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <TypeBadge type={item.type} />
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569" }}>
                          {item.company_name ? item.company_name : "—"}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569" }}>
                          {item.email}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569" }}>
                          {item.phone ? item.phone : "—"}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <button
                              type="button"
                              onClick={() => handleEditClick(item)}
                              title="Edit client"
                              style={{ border: "1px solid #e2e8f0", backgroundColor: "#ffffff", color: "#294b5a", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-pen-to-square" style={{ fontSize: "13px" }}></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(index)}
                              title="Delete client"
                              style={{ border: "1px solid #e2e8f0", backgroundColor: "#ffffff", color: "#dc2626", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: "13.5px" }}>
                        <i className="fa-solid fa-users-slash mb-2" style={{ fontSize: "24px", color: "#cbd5e1", display: "block" }}></i>
                        {searchTerm ? "No clients found matching your search." : "No clients found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {clients.length > 0 && lastPage > 1 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      color: currentPage === 1 ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Previous
                  </button>
                  {getPaginationRange(currentPage, lastPage).map((item, index) =>
                    item === "..." ? (
                      <span key={`dots-${index}`} style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: 600 }}>...</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => handlePageChange(item as number)}
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "12.5px",
                          fontWeight: 600,
                          borderRadius: "6px",
                          border: currentPage === item ? "none" : "1px solid #cbd5e1",
                          backgroundColor: currentPage === item ? "#294b5a" : "#fff",
                          color: currentPage === item ? "#fff" : "#334155",
                          cursor: "pointer",
                        }}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      backgroundColor: "#fff",
                      color: currentPage === lastPage ? "#94a3b8" : "#294b5a",
                      cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL DELETE */}
      {showDeleteModal && selectedRow !== null && (
        <div style={modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "#fff", borderRadius: "16px", width: "100%", maxWidth: "440px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "28px 24px 20px", textAlign: "center" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "#fef2f2", border: "1px solid #fecaca", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>
              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px" }}>Delete client?</h2>
              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0 }}>
                This action cannot be undone. All client profile details and history will be permanently removed.
              </p>
              {clients[selectedRow] && (
                <div style={{ marginTop: "16px", padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "left" }}>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>
                    {clients[selectedRow].firstname} {clients[selectedRow].lastname}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>{clients[selectedRow].email}</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "12px", padding: "16px 24px 20px" }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ ...ghostBtn, flex: 1, justifyContent: "center" }}>Cancel</button>
              <button onClick={handleConfirmDelete} style={{ flex: 1, padding: "10px 16px", fontSize: "13.5px", fontWeight: 600, color: "#fff", backgroundColor: "#dc2626", border: "none", borderRadius: "8px", cursor: "pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDIT CLIENT */}
      {showEditModal && (
        <div style={modalOverlay} onClick={() => setShowEditModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        width: "100%",
        maxWidth: "600px",
        maxHeight: "90vh",
        overflowY: "auto",
        overflowX: "hidden",
        border: "1px solid #e2e8f0",
      }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #edf2f7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ fontSize: "17px", fontWeight: 700, color: "#294b5a", margin: 0 }}>
                Edit Client Profile
              </h2>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "18px" }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div style={{ padding: "24px" }} className="row g-3">
                {/* First Name */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    First Name <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={inputStyle}
                    value={editFormData.firstname}
                    onChange={(e) => setEditFormData({ ...editFormData, firstname: e.target.value })}
                  />
                </div>

                {/* Last Name */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    Last Name <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={inputStyle}
                    value={editFormData.lastname}
                    onChange={(e) => setEditFormData({ ...editFormData, lastname: e.target.value })}
                  />
                </div>

                {/* Email */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    Email Address <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="email"
                    required
                    style={inputStyle}
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  />
                </div>

                {/* Phone */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    Phone Number <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={inputStyle}
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  />
                </div>

                {/* Client Type */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    Client Type <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <select
                    style={{ ...inputStyle, cursor: "pointer" }}
                    value={editFormData.type}
                    onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                  >
                    <option value="individual">Individual (Particulier)</option>
                    <option value="company">Company (Business)</option>
                  </select>
                </div>

                {/* Company Name (Conditionnel) */}
                {editFormData.type === "company" && (
                  <div className="col-12 col-md-6">
                    <label style={labelStyle}>Company Name</label>
                    <input
                      type="text"
                      style={inputStyle}
                      placeholder="e.g. Acme Corp"
                      value={editFormData.company_name}
                      onChange={(e) => setEditFormData({ ...editFormData, company_name: e.target.value })}
                    />
                  </div>
                )}

                {/* Country */}
                <div className="col-12 col-md-6">
                  <label style={labelStyle}>
                    Country <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={inputStyle}
                    value={editFormData.country}
                    onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                  />
                </div>

                {/* Address */}
                <div className="col-12">
                  <label style={labelStyle}>
                    Address <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={inputStyle}
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #edf2f7",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={ghostBtn}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={primaryBtn}
                >
                  {editLoading ? (
                    <>
                      <i className="fa-solid fa-circle-notch fa-spin"></i> Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListClient;